import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { buildSignupTrackingProperties, captureCalendlyEvent, captureResourceOpen } from '../utils/analytics';

const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_SCHEDULING_URL = 'https://calendly.com/yiwolf-medhug/30min';
const SUPPORTED_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CALENDLY_EVENT_MAP = {
  'calendly.profile_page_viewed': 'calendly_widget_loaded',
  'calendly.date_and_time_selected': 'calendly_date_selected',
  'calendly.event_scheduled': 'calendly_event_scheduled',
};

let calendlyScriptPromise;

function loadCalendlyScript() {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (window.Calendly) {
    return Promise.resolve(window.Calendly);
  }

  if (!calendlyScriptPromise) {
    calendlyScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
      const script = existingScript || document.createElement('script');

      const handleLoad = () => {
        script.dataset.loaded = 'true';
        resolve(window.Calendly);
      };

      const handleError = () => {
        reject(new Error('Calendly embed failed to load.'));
      };

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });

      if (!existingScript) {
        script.src = CALENDLY_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
    });
  }

  return calendlyScriptPromise;
}

function isCalendlyEvent(event) {
  return event.origin === 'https://calendly.com' && event.data?.event?.startsWith('calendly.');
}

function buildCalendlyUrl(search) {
  const currentParams = new URLSearchParams(search);
  const calendlyParams = new URLSearchParams();

  SUPPORTED_UTM_PARAMS.forEach((paramName) => {
    const value = currentParams.get(paramName);

    if (value) {
      calendlyParams.set(paramName, value);
    }
  });

  const queryString = calendlyParams.toString();

  return queryString ? `${CALENDLY_SCHEDULING_URL}?${queryString}` : CALENDLY_SCHEDULING_URL;
}

function SignupPage() {
  const location = useLocation();
  const embedRef = useRef(null);
  const seenEventsRef = useRef(new Set());
  const [loadError, setLoadError] = useState(false);
  const [booked, setBooked] = useState(false);
  const search = location.search;
  const trackingProperties = useMemo(
    () => buildSignupTrackingProperties(search, location.state),
    [search, location.state]
  );
  const embedUrl = useMemo(() => buildCalendlyUrl(search), [search]);

  useEffect(() => {
    seenEventsRef.current.clear();
  }, [search]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!isCalendlyEvent(event)) {
        return;
      }

      const mappedEventName = CALENDLY_EVENT_MAP[event.data.event];

      if (!mappedEventName || seenEventsRef.current.has(mappedEventName)) {
        return;
      }

      seenEventsRef.current.add(mappedEventName);
      captureCalendlyEvent(mappedEventName, {
        ...trackingProperties,
      });

      if (mappedEventName === 'calendly_event_scheduled') {
        setBooked(true);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [trackingProperties]);

  useEffect(() => {
    let cancelled = false;
    const parentElement = embedRef.current;

    setLoadError(false);

    if (!parentElement) {
      return undefined;
    }

    loadCalendlyScript()
      .then((Calendly) => {
        if (cancelled || !Calendly?.initInlineWidget) {
          return;
        }

        parentElement.innerHTML = '';
        Calendly.initInlineWidget({
          url: embedUrl,
          parentElement,
          resize: true,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      });

    return () => {
      cancelled = true;

      parentElement.innerHTML = '';
    };
  }, [embedUrl]);

  return (
    <div className="bg-[#f5f5f7]">
      {/* Info section */}
      <section className="px-4 pt-10 pb-2 md:px-8 md:pt-16 md:pb-4">
        <div className="mx-auto max-w-[980px] text-center">
          <h1 className="text-3xl md:text-4xl xl:text-[44px] font-semibold text-[#1d1d1f] leading-tight">
            Get started with MedHug
          </h1>
          <p className="mt-3 text-[17px] md:text-lg text-[#6e6e73] leading-relaxed max-w-2xl mx-auto">
            Book a 30-minute onboarding call. We&apos;ll walk you through the platform and get your team set up.
          </p>

          {/* Trust badges */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-[#1d1d1f] shadow-sm border border-black/5">
              <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Free platform
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-[#1d1d1f] shadow-sm border border-black/5">
              <svg className="w-4 h-4 text-[#6e6e73]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              30-minute call
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-[#1d1d1f] shadow-sm border border-black/5">
              <svg className="w-4 h-4 text-[#6e6e73]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              No commitment
            </span>
          </div>

          {/* What to expect */}
          <div className="mt-8 mx-auto max-w-xl text-left">
            <p className="text-[13px] font-semibold uppercase tracking-wider text-[#6e6e73] mb-3">What to expect</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#1d1d1f] text-white text-[11px] font-semibold">1</div>
                <p className="text-[15px] text-[#1d1d1f] leading-relaxed">See real-time coverage and cost data in action</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#1d1d1f] text-white text-[11px] font-semibold">2</div>
                <p className="text-[15px] text-[#1d1d1f] leading-relaxed">Get your practice configured and ready to go</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#1d1d1f] text-white text-[11px] font-semibold">3</div>
                <p className="text-[15px] text-[#1d1d1f] leading-relaxed">Ask questions about cost details, coverage requirements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendly embed card */}
      <section className="px-4 py-6 md:px-8 md:py-8">
        <div className="mx-auto max-w-[980px] rounded-[32px] border border-black/5 bg-white p-3 shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
          <div className="rounded-[24px] bg-[#f5f5f7] p-4 md:p-5">
            <div className="flex items-center justify-between gap-4 px-2 pb-4">
              <div>
                <p className="text-[14px] text-[#6e6e73]">Choose a slot that works for you.</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-[#1d1d1f] text-white flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect x="3" y="4" width="18" height="18" rx="3" />
                  <path d="M3 10h18" />
                  <path d="M8 14h.01" />
                  <path d="M12 14h.01" />
                  <path d="M16 14h.01" />
                  <path d="M8 18h.01" />
                  <path d="M12 18h.01" />
                </svg>
              </div>
            </div>

            {booked && (
              <div className="mb-4 rounded-[20px] border border-green-100 bg-green-50 px-6 py-6">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[15px] font-semibold text-[#1d1d1f]">You're all set!</p>
                    <p className="mt-1 text-[14px] leading-relaxed text-[#6e6e73]">
                      A calendar invite is on its way. While you wait, explore some of our resources:
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Link
                        to="/resources/glp1-bridge"
                        onClick={() => captureResourceOpen('glp1-bridge', {
                          resource_title: 'Medicare GLP-1 Bridge Resource Center',
                          resource_type: 'resource-center',
                          source: 'signup-confirmation',
                        })}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-[#1d1d1f] shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        GLP-1 Bridge Resources
                        <svg className="w-3.5 h-3.5 text-[#6e6e73]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                      </Link>
                      <Link
                        to="/resources/m3p"
                        onClick={() => captureResourceOpen('m3p', {
                          resource_title: 'Medicare Prescription Payment Plan (M3P): Overview & FAQs',
                          resource_type: 'clinical-guide',
                          source: 'signup-confirmation',
                        })}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium text-[#1d1d1f] shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        M3P Guide
                        <svg className="w-3.5 h-3.5 text-[#6e6e73]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loadError ? (
              <div className="rounded-[20px] border border-red-100 bg-red-50 px-6 py-8 text-left">
                <p className="text-[15px] font-semibold text-[#1d1d1f] mb-2">Calendly didn&apos;t load inside the page.</p>
                <p className="text-[14px] text-[#6e6e73] leading-relaxed mb-5">
                  The booking link is still available directly if the embed is blocked by the browser or network.
                </p>
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1d1d1f] px-5 py-3 text-sm font-medium text-white hover:bg-[#333]"
                >
                  Open Calendly directly
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17L17 7" />
                    <path d="M8 7h9v9" />
                  </svg>
                </a>
              </div>
            ) : (
              <div
                ref={embedRef}
                id="calendly-embed"
                className="min-w-[320px] overflow-hidden rounded-[20px] bg-white"
                style={{ minHeight: 700 }}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SignupPage;
